/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Author: Amatsukast (https://sketchfab.com/Amatsukast)
License: CC-BY-NC-SA-4.0 (http://creativecommons.org/licenses/by-nc-sa/4.0/)
Source: https://sketchfab.com/3d-models/divergence-meter-steinsgate-b43abf482d30435fa0683b765deee31b
Title: Divergence Meter (Steins;Gate)

NIXIE TUBE DIGIT MAPPING:
========================
8 digit positions total (left to right)

Position | Material Range     | Usage
---------|--------------------|-----------------
1        | 280-290           | First digit (leftmost)
2        | 238-248           | Second digit  
3        | 154-164           | Third digit
4        | 112-122           | Fourth digit
5        | 28-38             | Fifth digit
6        | 70-80             | Sixth digit
7        | 196-206           | Seventh digit
8        | 402-412           | Eighth digit (rightmost)

Special positions for clock mode:
- Position 3: First dot (material 164)
- Position 6: Second dot (material 80)

Each range represents digits 0-9 in order:
- Material_X+0 = digit 0
- Material_X+1 = digit 1  
- Material_X+2 = digit 2
- ...
- Material_X+9 = digit 9
- Material_X+10 = dot (for positions 3 & 6)

Example: To show digit "5" in position 1, use materials.number_on_mt for material 285
*/

import React, { useState, useEffect } from 'react'
import { useGLTF } from '@react-three/drei'

interface ModelProps {
  displayMode?: 'clock' | 'custom' | 'counter'
  customValue?: number | string
  showDots?: boolean[]  // Array of 2 booleans for the two dot positions
  [key: string]: any
}

export const Model = React.memo((props: ModelProps) => {
  const { 
    displayMode = 'clock', 
    customValue = 0, 
    showDots = [true, true],
    ...restProps 
  } = props
  
  const { nodes, materials } = useGLTF('/scene.gltf') as any
  const [currentTime, setCurrentTime] = useState(new Date())
  const [counter, setCounter] = useState(0)

  // Update time every second for clock mode
  useEffect(() => {
    if (displayMode === 'clock') {
      const timer = setInterval(() => {
        setCurrentTime(new Date())
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [displayMode])

  // Update counter every second for counter mode
  useEffect(() => {
    if (displayMode === 'counter') {
      const timer = setInterval(() => {
        setCounter(prev => (prev + 1) % 100000000) // Reset at 8 digits max
      }, 500) // Update every 500ms instead of 100ms for better performance
      return () => clearInterval(timer)
    }
  }, [displayMode])

  // Generic function to get 8 digits for display
  const getDisplayDigits = (): number[] => {
    switch (displayMode) {
      case 'clock': {
        const hours = currentTime.getHours().toString().padStart(2, '0')
        const minutes = currentTime.getMinutes().toString().padStart(2, '0')
        const seconds = currentTime.getSeconds().toString().padStart(2, '0')
        return [
          parseInt(hours[0]),    // position 1: hours tens
          parseInt(hours[1]),    // position 2: hours ones
          0, // position 3: first dot (handled separately)
          parseInt(minutes[0]),  // position 4: minutes tens
          parseInt(minutes[1]),  // position 5: minutes ones
          0, // position 6: second dot (handled separately)
          parseInt(seconds[0]),  // position 7: seconds tens
          parseInt(seconds[1])   // position 8: seconds ones
        ]
      }
      case 'custom': {
        const valueStr = customValue.toString()
        
        // Check if it contains a dot
        if (valueStr.includes('.')) {
          const parts = valueStr.split('.')
          const beforeDot = parts[0] || ''
          const afterDot = parts[1] || ''
          
          // Format: X.XXXXXX (1 digit before dot, up to 6 after)
          const beforePadded = beforeDot.padStart(1, '0').slice(-1) // Take last 1 digit
          const afterPadded = afterDot.padEnd(6, '0').slice(0, 6)   // Take first 6 digits
          
          return [
            parseInt(beforePadded[0]) || 0,  // position 1
            0, // position 2: dot (handled separately)
            parseInt(afterPadded[0]) || 0,   // position 3
            parseInt(afterPadded[1]) || 0,   // position 4
            parseInt(afterPadded[2]) || 0,   // position 5
            parseInt(afterPadded[3]) || 0,   // position 6
            parseInt(afterPadded[4]) || 0,   // position 7
            parseInt(afterPadded[5]) || 0    // position 8
          ]
        } else {
          // Regular 8-digit number
          const valueStr8 = valueStr.padStart(8, '0').slice(-8)
          return valueStr8.split('').map(d => parseInt(d))
        }
      }
      case 'counter': {
        const counterStr = counter.toString().padStart(8, '0')
        return counterStr.split('').map(d => parseInt(d))
      }
      default:
        return [0, 0, 0, 0, 0, 0, 0, 0]
    }
  }

  const digits = getDisplayDigits()

  // Check if custom mode has a dot
  const hasCustomDot = displayMode === 'custom' && customValue.toString().includes('.')


  // Generic helper function to get material for any digit position
  const getMaterial = (baseNumber: number, digit: number) => {
    return (materialIndex: number) => {
      const digitIndex = materialIndex - baseNumber
      if (digitIndex === digit) {
        return materials.number_on_mt
      }
      return materials.number_off_mt
    }
  }

  // Material functions for each position - now using generic digit array
  const getPosition1Material = getMaterial(280, digits[0])   // position 1
  const getPosition2Material = (materialIndex: number) => {
    // In custom mode with dot, show dot at material 248 (last in 238-248 range)
    if (hasCustomDot && materialIndex === 248) {
      return materials.number_on_mt
    }
    // In custom mode with dot, turn off all digit materials in position 2
    if (hasCustomDot) {
      return materials.number_off_mt
    }
    // Otherwise show the digit normally
    return getMaterial(238, digits[1])(materialIndex)
  }
  const getPosition3Material = (materialIndex: number) => {
    // In clock mode, turn off all digits in position 3 (only show dot)
    if (displayMode === 'clock') {
      return materials.number_off_mt
    }
    // In other modes, show the actual digit
    return getMaterial(154, digits[2])(materialIndex)
  }
  const getFirstDotMaterial = (materialIndex: number) => {
    // Position 3 dots: show dot at material 164 if showDots[0] is true
    return (materialIndex === 164 && showDots[0]) ? materials.number_on_mt : materials.number_off_mt
  }
  const getPosition4Material = getMaterial(112, digits[3]) // position 4
  const getPosition5Material = getMaterial(28, digits[4])  // position 5
  const getPosition6Material = (materialIndex: number) => {
    // In clock mode, turn off all digits in position 6 (only show dot)
    if (displayMode === 'clock') {
      return materials.number_off_mt
    }
    // In other modes, show the actual digit
    return getMaterial(70, digits[5])(materialIndex)
  }
  const getSecondDotMaterial = (materialIndex: number) => {
    // Position 6 dots: show dot at material 80 if showDots[1] is true
    return (materialIndex === 80 && showDots[1]) ? materials.number_on_mt : materials.number_off_mt
  }
  const getPosition7Material = getMaterial(196, digits[6]) // position 7
  const getPosition8Material = getMaterial(402, digits[7]) // position 8

  return (
    <group {...restProps} dispose={null}>
      <group rotation={[-Math.PI / 2, 0, 0]} scale={0.02}>
        <group rotation={[Math.PI / 2, 0, 0]}>
          <mesh
            geometry={nodes.defaultMaterial.geometry}
            material={materials.glass_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_1.geometry}
            material={materials.glass_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_2.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_3.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_4.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_5.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_6.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_7.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_8.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_9.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_10.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_11.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_12.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_13.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_14.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_15.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_16.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_17.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_18.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_19.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_20.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_21.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_22.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_23.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_24.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_25.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_26.geometry}
            material={materials.display_mt}
          />
          <mesh
            geometry={nodes.defaultMaterial_27.geometry}
            material={materials.display_mt}
          />
          // XXXX0XXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_28.geometry}
            material={getPosition5Material(28)}
          />
          // xxxx1xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_29.geometry}
            material={getPosition5Material(29)}
          />
          // xxxx2xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_30.geometry}
            material={getPosition5Material(30)}
          />
          // xxxx3xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_31.geometry}
            material={getPosition5Material(31)}
          />
          // xxxx4xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_32.geometry}
            material={getPosition5Material(32)}
          />
          // xxxx5xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_33.geometry}
            material={getPosition5Material(33)}
          />
          // xxxx6xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_34.geometry}
            material={getPosition5Material(34)}
          />
          // xxxx7xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_35.geometry}
            material={getPosition5Material(35)}
          />
          // xxxx8xxx
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_36.geometry}
            material={getPosition5Material(36)}
          />
          // XXXX9XXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_37.geometry}
            material={getPosition5Material(37)}
          />
          // XXXX.XXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_38.geometry}
            material={getPosition5Material(38)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_39.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_40.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_41.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_42.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_43.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_44.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_45.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_46.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_47.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_48.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_49.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_50.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_51.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_52.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_53.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_54.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_55.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_56.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_57.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_58.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_59.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_60.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_61.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_62.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_63.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_64.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_65.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_66.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_67.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_68.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_69.geometry}
            material={materials.display_mt}
          />
          // XXXXX0XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_70.geometry}
            material={getPosition6Material(70)}
          />
          // XXXXX1XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_71.geometry}
            material={getPosition6Material(71)}
          />
          // XXXXX2XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_72.geometry}
            material={getPosition6Material(72)}
          />
          // XXXXX3XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_73.geometry}
            material={getPosition6Material(73)}
          />
          // XXXXX4XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_74.geometry}
            material={getPosition6Material(74)}
          />
          // XXXXX5XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_75.geometry}
            material={getPosition6Material(75)}
          />
          // XXXXX6XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_76.geometry}
            material={getPosition6Material(76)}
          />
          // XXXXX7XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_77.geometry}
            material={getPosition6Material(77)}
          />
          // XXXXX8XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_78.geometry}
            material={getPosition6Material(78)}
          />
          // XXXXX9XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_79.geometry}
            material={getPosition6Material(79)}
          />
          // XXXXX.XX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_80.geometry}
            material={getSecondDotMaterial(80)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_81.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_82.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_83.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_84.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_85.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_86.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_87.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_88.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_89.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_90.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_91.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_92.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_93.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_94.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_95.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_96.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_97.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_98.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_99.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_100.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_101.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_102.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_103.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_104.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_105.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_106.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_107.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_108.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_109.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_110.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_111.geometry}
            material={materials.display_mt}
          />
          // XXX0XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_112.geometry}
            material={getPosition4Material(112)}
          />
          // XXX1XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_113.geometry}
            material={getPosition4Material(113)}
          />
          // XXX2XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_114.geometry}
            material={getPosition4Material(114)}
          />
          // XXX3XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_115.geometry}
            material={getPosition4Material(115)}
          />
          // XXX4XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_116.geometry}
            material={getPosition4Material(116)}
          />
          // XXX5XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_117.geometry}
            material={getPosition4Material(117)}
          />
          // XXX6XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_118.geometry}
            material={getPosition4Material(118)}
          />
          // XXX7XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_119.geometry}
            material={getPosition4Material(119)}
          />
          // XXX8XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_120.geometry}
            material={getPosition4Material(120)}
          />
          // XXX9XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_121.geometry}
            material={getPosition4Material(121)}
          />
          // XXX.XXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_122.geometry}
            material={getPosition4Material(122)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_123.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_124.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_125.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_126.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_127.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_128.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_129.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_130.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_131.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_132.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_133.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_134.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_135.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_136.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_137.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_138.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_139.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_140.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_141.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_142.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_143.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_144.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_145.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_146.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_147.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_148.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_149.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_150.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_151.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_152.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_153.geometry}
            material={materials.display_mt}
          />
          // XX0XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_154.geometry}
            material={getPosition3Material(154)}
          />
          // XX1XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_155.geometry}
            material={getPosition3Material(155)}
          />
          // XX2XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_156.geometry}
            material={getPosition3Material(156)}
          />
          // XX3XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_157.geometry}
            material={getPosition3Material(157)}
          />
          // XX4XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_158.geometry}
            material={getPosition3Material(158)}
          />
          // XX5XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_159.geometry}
            material={getPosition3Material(159)}
          />
          // XX6XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_160.geometry}
            material={getPosition3Material(160)}
          />
          // XX7XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_161.geometry}
            material={getPosition3Material(161)}
          />
          // XX8XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_162.geometry}
            material={getPosition3Material(162)}
          />
          // XX9XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_163.geometry}
            material={getPosition3Material(163)}
          />
          // XX.XXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_164.geometry}
            material={getFirstDotMaterial(164)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_165.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_166.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_167.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_168.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_169.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_170.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_171.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_172.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_173.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_174.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_175.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_176.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_177.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_178.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_179.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_180.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_181.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_182.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_183.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_184.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_185.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_186.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_187.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_188.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_189.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_190.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_191.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_192.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_193.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_194.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_195.geometry}
            material={materials.display_mt}
          />
          // XXXXXX0X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_196.geometry}
            material={getPosition7Material(196)}
          />
          // XXXXXX1X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_197.geometry}
            material={getPosition7Material(197)}
          />
          // XXXXXX2X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_198.geometry}
            material={getPosition7Material(198)}
          />
          // XXXXXX3X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_199.geometry}
            material={getPosition7Material(199)}
          />
          // XXXXXX4X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_200.geometry}
            material={getPosition7Material(200)}
          />
          // XXXXXX5X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_201.geometry}
            material={getPosition7Material(201)}
          />
          // XXXXXX6X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_202.geometry}
            material={getPosition7Material(202)}
          />
          // XXXXXX7X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_203.geometry}
            material={getPosition7Material(203)}
          />
          // XXXXXX8X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_204.geometry}
            material={getPosition7Material(204)}
          />
          // XXXXXX9X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_205.geometry}
            material={getPosition7Material(205)}
          />
          // XXXXXX.X
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_206.geometry}
            material={getPosition7Material(206)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_207.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_208.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_209.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_210.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_211.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_212.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_213.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_214.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_215.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_216.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_217.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_218.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_219.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_220.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_221.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_222.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_223.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_224.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_225.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_226.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_227.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_228.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_229.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_230.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_231.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_232.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_233.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_234.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_235.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_236.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_237.geometry}
            material={materials.display_mt}
          />
          // X0XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_238.geometry}
            material={getPosition2Material(238)}
          />
          // X1XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_239.geometry}
            material={getPosition2Material(239)}
          />
          // X2XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_240.geometry}
            material={getPosition2Material(240)}
          />
          // X3XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_241.geometry}
            material={getPosition2Material(241)}
          />
          // X4XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_242.geometry}
            material={getPosition2Material(242)}
          />
          // X5XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_243.geometry}
            material={getPosition2Material(243)}
          />
          // X6XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_244.geometry}
            material={getPosition2Material(244)}
          />
          // X7XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_245.geometry}
            material={getPosition2Material(245)}
          />
          // X8XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_246.geometry}
            material={getPosition2Material(246)}
          />
          // X9XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_247.geometry}
            material={getPosition2Material(247)}
          />
          // X.XXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_248.geometry}
            material={getPosition2Material(248)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_249.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_250.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_251.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_252.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_253.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_254.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_255.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_256.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_257.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_258.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_259.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_260.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_261.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_262.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_263.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_264.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_265.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_266.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_267.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_268.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_269.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_270.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_271.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_272.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_273.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_274.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_275.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_276.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_277.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_278.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_279.geometry}
            material={materials.display_mt}
          />
          // 0XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_280.geometry}
            material={getPosition1Material(280)}
          />
          // 1XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_281.geometry}
            material={getPosition1Material(281)}
          />
          // 2XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_282.geometry}
            material={getPosition1Material(282)}
          />
          // 3XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_283.geometry}
            material={getPosition1Material(283)}
          />
          // 4XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_284.geometry}
            material={getPosition1Material(284)}
          />
          // 5XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_285.geometry}
            material={getPosition1Material(285)}
          />
          // 6XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_286.geometry}
            material={getPosition1Material(286)}
          />
          // 7XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_287.geometry}
            material={getPosition1Material(287)}
          />
          // 8XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_288.geometry}
            material={getPosition1Material(288)}
          />
          // 9XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_289.geometry}
            material={getPosition1Material(289)}
          />
          // .XXXXXXX
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_290.geometry}
            material={getPosition1Material(290)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_291.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_292.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_293.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_294.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_295.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_296.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_297.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_298.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_299.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_300.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_301.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_302.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_303.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_304.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_305.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_306.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_307.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_308.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_309.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_310.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_311.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_312.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_313.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_314.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_315.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_316.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_317.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_318.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_319.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_320.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_321.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_322.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_323.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_324.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_325.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_326.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_327.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_328.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_329.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_330.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_331.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_332.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_333.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_334.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_335.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_336.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_337.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_338.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_339.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_340.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_341.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_342.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_343.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_344.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_345.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_346.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_347.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_348.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_349.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_350.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_351.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_352.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_353.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_354.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_355.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_356.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_357.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_358.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_359.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_360.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_361.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_362.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_363.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_364.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_365.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_366.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_367.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_368.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_369.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_370.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_371.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_372.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_373.geometry}
            material={materials.parts_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_374.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_375.geometry}
            material={materials.glass_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_376.geometry}
            material={materials.mesh_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_377.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_378.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_379.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_380.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_381.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_382.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_383.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_384.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_385.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_386.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_387.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_388.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_389.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_390.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_391.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_392.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_393.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_394.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_395.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_396.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_397.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_398.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_399.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_400.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_401.geometry}
            material={materials.display_mt}
          />
          // XXXXXXX0
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_402.geometry}
            material={getPosition8Material(402)}
          />
          // XXXXXXX1
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_403.geometry}
            material={getPosition8Material(403)}
          />
          // XXXXXXX2
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_404.geometry}
            material={getPosition8Material(404)}
          />
          // XXXXXXX3
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_405.geometry}
            material={getPosition8Material(405)}
          />
          // XXXXXXX4
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_406.geometry}
            material={getPosition8Material(406)}
          />
          // XXXXXXX5
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_407.geometry}
            material={getPosition8Material(407)}
          />
          // XXXXXXX6
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_408.geometry}
            material={getPosition8Material(408)}
          />
          // XXXXXXX7
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_409.geometry}
            material={getPosition8Material(409)}
          />
          // XXXXXXX8
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_410.geometry}
            material={getPosition8Material(410)}
          />
          // XXXXXXX9
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_411.geometry}
            material={getPosition8Material(411)}
          />
          // XXXXXXX.
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_412.geometry}
            material={getPosition8Material(412)}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_413.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_414.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_415.geometry}
            material={materials.display_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_416.geometry}
            material={materials.body01_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_417.geometry}
            material={materials.body01_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_418.geometry}
            material={materials.body01_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_419.geometry}
            material={materials.body02_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_420.geometry}
            material={materials.body02_mt}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.defaultMaterial_421.geometry}
            material={materials.body02_mt}
          />
        </group>
      </group>
    </group>
  )
})

useGLTF.preload('/scene.gltf')
